import { makeAutoObservable } from 'mobx';
import { Calculation } from '../models/Calculation';
import { Entity } from '../models/Entity';
import { computedFn } from 'mobx-utils';
import {
  DoNotRechargeId,
  RuleTransactionType,
  RuleType,
  RuleVisibility,
} from '../../config/Constants';
import { Rule } from '../models/Rule';
import translate, { Translations } from '../../language/translate';
import { RuleApi } from '../../api/RuleApi';

/**
 * This store handles calculation-specific rule configurations. For instace,
 * - Previous versions of rules which are being used
 * - Adhoc rules created just for this calculation
 * - Point in time rulesets for locked calculations (soon)
 */
export class CalculationRulesStore {
  entities: Set<Entity> = new Set();
  calculation: Calculation = null;

  requiredRules: Set<number> = new Set();

  baseRules: Rule[] = [];
  adhocRules: Rule[] = [];
  outdatedRules: Rule[] = [];

  ruleApptIdMap: Map<number, Rule> = new Map();
  ruleApptIdMapBuilt: boolean = false;

  constructor(calculation: Calculation) {
    this.calculation = calculation;
    makeAutoObservable(this);
  }

  addEntity = (entity: Entity) => {
    this.entities.add(entity);
  };

  expectRule = (ruleApportionmentId: number) => {
    this.requiredRules.add(ruleApportionmentId);
  };

  // TODO: Optimise this
  getBaseRule = (ruleApptId: number) =>
    this.baseRules.find(r => r.ruleApportionmentId === ruleApptId);

  initialiseRuleset = async (): Promise<Rule[]> => {
    // Future: if the calculation is locked, fetch the
    // point-in-time rules from the backend
    this.baseRules = [...this.entities.values()].flatMap(e => e.rules);

    return Promise.all(
      [...this.requiredRules.values()].map(ruleId => {
        const baseRule = this.getBaseRule(ruleId);
        if (baseRule) return Promise.resolve(baseRule);

        return this.loadRuleApportionmentId(
          this.calculation.groupId,
          ruleId
        ).then(rule => {
          if (rule.visibility === RuleVisibility.CALCULATION) {
            this.adhocRules.push(rule);
          } else {
            this.outdatedRules.push(rule);
          }

          return rule;
        });
      })
    );
  };

  loadRuleApportionmentId = (
    groupId: number,
    ruleApptId: number
  ): Promise<Rule> => {
    return RuleApi.getRuleApportionment(groupId, ruleApptId).then(response => {
      const rule = Rule.fromApi(response, groupId);
      rule.isLatest = false;

      return rule;
    });
  };

  // Memoize the response of this method for performance
  ruleOptions = computedFn(
    (
      entity: Entity,
      ruleTransactionType: RuleTransactionType,
      numSelected: number
    ): Map<string, Map<number, string>> => {
      const currentKey = 'Current Rules';
      const outdatedKey = 'Outdated Rules';
      const adhocKey = 'Ad-hoc Recharges';

      const ruleFilter = (r: Rule) =>
        r.creatingEntity === entity.id &&
        r.transactionType === ruleTransactionType;
      const ruleMapper = (r: Rule): [number, string] => [
        r.ruleApportionmentId,
        r.name,
      ];
      const ruleSorter = (a: [number, string], b: [number, string]) =>
        a[1].localeCompare(b[1]);
      let rules = this.baseRules.filter(ruleFilter);

      const hasDefault = rules.find(r =>
        [RuleType.NONE, RuleType.NONE_BALANCE, RuleType.NONE_SALES].includes(
          r.type
        )
      );
      const adhocRules = this.adhocRules.filter(ruleFilter);

      // Set up the adhoc rules first
      const rulesOptions = new Map([
        [
          adhocKey,
          new Map<number, string>(adhocRules.map(ruleMapper).sort(ruleSorter)),
        ],
      ]);

      rulesOptions
        .get(adhocKey)
        .set(0, translate(Translations.Transactions.CREATE_ADHOC, { count: numSelected }));

      rulesOptions.set(
        currentKey,
        new Map(rules.map(ruleMapper).sort(ruleSorter))
      );

      if (!hasDefault) {
        rulesOptions
          .get(currentKey)
          .set(DoNotRechargeId, `Don't recharge (${ruleTransactionType})`);
      }

      // If there are outdated rules, add those next
      const entityOutdatedRules = this.outdatedRules.filter(ruleFilter);
      if (entityOutdatedRules.length > 0) {
        rulesOptions.set(
          outdatedKey,
          new Map(entityOutdatedRules.map(ruleMapper).sort(ruleSorter))
        );
      }

      return rulesOptions;
    }
  );

  allRulesForEntity = computedFn((entityId: number) => {
    const filter = (r: Rule) => r.creatingEntity === entityId;

    return this.baseRules
      .filter(filter)
      .concat(this.adhocRules.filter(filter))
      .concat(this.outdatedRules.filter(filter));
  });

  buildRuleApptIdMap = () => {
    if (!this.ruleApptIdMapBuilt) {
      this.baseRules
        .concat(this.adhocRules)
        .concat(this.outdatedRules)
        .map(r => this.ruleApptIdMap.set(r.ruleApportionmentId, r));
      this.ruleApptIdMapBuilt = true;
    }
  };

  getRuleByRuleApportionmentId = (ruleApptId: number): Rule => {
    this.buildRuleApptIdMap();
    return this.ruleApptIdMap.get(ruleApptId) || null;
  };
}
